import { useEffect, useRef, type FunctionComponent, type ReactElement, useState } from 'react';
import 'reflect-metadata';
import './App.css';
import { Page, MainArea, ViewLayout, Sidebar } from './components/layout';
import { TYPES } from '@/engine/ioc';
import type { AppController } from '@/engine/app/App.controller';
import type { ViewsService } from '@/engine/app/Views.service';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react';
import { type IReactionDisposer, reaction } from 'mobx';
import { useParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { SideHeader } from './components/sideHeader/SideHeader';
import { BrowserNotificationModal } from './components/modals/browserNotification/BrowserNotificationModal';
import { ScreenNotificationModal } from './components/modals/screenNotification/ScreenNotificationModal';
import { WebglNotificationModal } from './components/modals/webglNotification/WebglNotificationModal';
import { JSNotificationModal } from './components/modals/jsErrorNotification/JSNotificationModal';
import { CustomThemeProvider } from './assets/branding/CustomThemeProvider';
import { useSurgerySpecificUiConfigValue } from './engine/app/modules/ui';
import { RightPanelTabContainer } from '@/components/layout/RightPanelTabContainer/RightPanelTabContainer.tsx';
import { OrientationPresets } from '@/components/orientationPresets';
import { FluoroCurve } from '@/components/fluoroCurve';
import { PlanningContainer } from '@/components/contentContainers/PlanningContainer.tsx';
import { LoadingOverlay } from '@/components/loading-animation/LoadingOverlay.tsx';
import { PhasesCurve } from '@/components/phasesCurve/phasesCurve.tsx';
import { CONTAINER_MARGIN } from '@/components/phasesCurve/interfaces.ts';
import { NavigationButtons } from './components/navigationButtons/NavigationButtons.tsx';
import { LearningGuide } from '@/engine/app/modules/learningGuide/index.ts';
import { ProgressOverlay } from './components/loading-animation/ProgressOverlay.tsx';
import { CineloopHover } from './components/loading-animation/CineloopHover.tsx';
import { ToastNotifications } from './components/toastNotifications/ToastNotifications.tsx';
import { useInitSaveCaseSubscriptionsAfterInitialization } from './hooks/useInitSaveCaseSubscriptionsAfterInitialization.ts';
import { DefaultHeaderPanel } from '@/components/header/HeaderPanel';
import { OrientationOverlay } from '@/components/orientationOverlay/OrientationOverlay';
import { Banners } from '@/engine/app/banner/Banners';

const DefaultSidebar = (): ReactElement => {
  const [isCurveRendered, setCurveIsRendered] = useState<boolean>(false);

  const handleCurveIsRendered = (isRendered: boolean): void => {
    setCurveIsRendered(isRendered);
  };

  return (
    <>
      <PhasesCurve handleCurveIsRendered={handleCurveIsRendered} />
      <RightPanelTabContainer marginTop={isCurveRendered ? CONTAINER_MARGIN : 0}>
        <PlanningContainer />
        <OrientationPresets />
        <FluoroCurve />
      </RightPanelTabContainer>
    </>
  );
};

function App(): ReactElement {
  const appController = useInjection<AppController>(TYPES.AppController);
  const [isSidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'l' && e.ctrlKey) {
        setSidebarOpen((prev) => !prev);
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handler);

    return (): void => window.removeEventListener('keydown', handler);
  }, []);

  const { caseId, packageId } = useParams();
  useEffect(() => {
    const initApp = async (): Promise<void> => {
      try {
        await appController.init(caseId ?? '455006', packageId ?? 'de2298db-76b6-4c44-a4c4-50d9775e75e9');
      } catch (error) {
        if (error instanceof Error && error.message.includes('401')) {
          console.log('Authentication error (401) detected - awaiting redirect to login page');
          return;
        }
        console.error('Error initializing app:', error);
        setError(error instanceof Error ? error : new Error(String(error)));
      }
    };

    void initApp();
  }, [appController, caseId, packageId]);

  if (error) {
    throw error;
  }

  const scrollableNodeRef = useRef<HTMLDivElement>();
  const viewService: ViewsService = useInjection<ViewsService>(TYPES.ViewsService);

  useEffect(() => {
    const disposer: IReactionDisposer = reaction(
      () => viewService.currentView,
      (): void => {
        scrollableNodeRef.current?.scrollTo?.(0, 0);
      }
    );

    return (): void => disposer();
  }, [viewService, appController.surgeryType]);

  useInitSaveCaseSubscriptionsAfterInitialization(appController);

  const SidebarComponent = useSurgerySpecificUiConfigValue<FunctionComponent>('sidebar.component', DefaultSidebar);
  const HeaderPanel = useSurgerySpecificUiConfigValue<FunctionComponent>('headerPanel.component', DefaultHeaderPanel);

  const SurgerySpecificMarkupInjection = useSurgerySpecificUiConfigValue<FunctionComponent>(
    'appMarkupInjection',
    () => null
  );

  return (
    <div>
      <ToastNotifications />
      <LearningGuide />
      <LoadingOverlay />
      <ProgressOverlay />
      <OrientationOverlay />
      {appController.hasAppInitializationCompleted && (
        <Container className={appController.isCineloopActive ? 'cinelooping' : ''}>
          <CustomThemeProvider>
            <WebglNotificationModal />
            <BrowserNotificationModal />
            <ScreenNotificationModal />
            <JSNotificationModal />
            <SurgerySpecificMarkupInjection />
            <Page>
              <HeaderContainer data-testid="headerPanel" className="header-panel">
                <CineloopHover />
                <HeaderPanel />
              </HeaderContainer>
              <MainAreaContainer
                style={{ marginRight: isSidebarOpen ? '0' : '-25rem' }}
                onTransitionEnd={(): void => void window.dispatchEvent(new Event('resize'))}
              >
                <Banners />
                <MainArea>
                  <ViewLayout />
                </MainArea>
                <Sidebar isOpen={isSidebarOpen}>
                  <CineloopHover />
                  <SideHeader />
                  <SideContent>
                    <SimpleBarStyled scrollableNodeProps={{ ref: scrollableNodeRef }} style={{ height: '100%' }}>
                      <SidebarContentWrapper>
                        <SidebarComponent />
                      </SidebarContentWrapper>
                    </SimpleBarStyled>
                  </SideContent>
                  <NavigationButtons />
                </Sidebar>
              </MainAreaContainer>
            </Page>
          </CustomThemeProvider>
        </Container>
      )}
    </div>
  );
}

export default observer(App);

const SimpleBarStyled = styled(SimpleBar)`
  .simplebar-track {
    z-index: 999;
  }
`;

const SideContent = styled.div`
  overflow: auto;
`;

const SidebarContentWrapper = styled.div`
  padding: 0 0.8em;
`;

const frameAnimation = keyframes`
  0% { opacity: 0 }
  70% { opacity: 0 }
  90% { opacity: 1 }
  100% { opacity: 1 }
`;

const Container = styled.div`
  animation: ${frameAnimation} 0.5s;
  height: 100%;
`;

const HeaderContainer = styled.div`
  flex: 0 0 auto;
  background-color: ${({ theme }): string => theme.customPalette.panels.header.background};
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 0.5em;
  pointer-events: none;
  box-shadow: -2px 0 3px rgb(80 90 98 / 80%);
  position: relative;
  z-index: 2;
  font-size: 1.3em;
  @media (max-width: 100rem) {
    font-size: 1.2em;
    padding: 0.4em;
  }
`;

const MainAreaContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  overflow: hidden;
  transition: margin-right 0.3s ease;
  position: relative;
`;
